import React from 'react';
import { Modal, Button, Spin, Space, Typography, Tooltip, List, Tag, Alert } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { SourceScene } from '../../../types';

const { Title, Text } = Typography;

interface ReviewResultModalProps {
  visible: boolean;
  onCancel: () => void;
  scene: SourceScene | null;
  result: {
    critiques: string[];
    issues: Array<{ tag: string; description: string }>;
    suggestedComment: string;
  } | null;
  onApply: () => void;
  loading: boolean;
}

const ReviewResultModal: React.FC<ReviewResultModalProps> = ({
  visible,
  onCancel,
  scene,
  result,
  onApply,
  loading,
}) => {
  const renderIssueTag = (tag: string) => {
    const tagConfig: Record<string, { color: string; label: string }> = {
      CAPTION_TOO_LONG: { color: 'error', label: 'Caption dài' },
      CAPTION_TOO_SHORT: { color: 'warning', label: 'Caption ngắn' },
      MISSING_IDEAS: { color: 'volcano', label: 'Thiếu ý' },
      BAD_RHYTHM: { color: 'purple', label: 'Nhịp điệu' },
      CHARACTER_MISMATCH: { color: 'magenta', label: 'Nhân vật' },
      VISUAL_INCONSISTENCY: { color: 'orange', label: 'Hình ảnh' },
      OTHER: { color: 'default', label: 'Khác' },
    };

    const config = tagConfig[tag] || { color: 'default', label: tag };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  return (
    <Modal
      title={`AI Review Scene - ${scene?.title}`}
      open={visible}
      onCancel={onCancel}
      width={700}
      footer={[
        <Button key="close" onClick={onCancel}>
          Đóng
        </Button>,
        result && (
          <Button
            key="apply"
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={onApply}
            loading={loading}
          >
            Sử dụng gợi ý để Re-extract
          </Button>
        )
      ]}
    >
      {!result ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin tip="AI đang phân tích và đối chiếu nội dung..." />
        </div>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {result.issues.length > 0 && (
            <div>
              <Title level={5}>Vấn đề chính (Issue Tags):</Title>
              <div style={{ marginBottom: 16 }}>
                {result.issues.map((issue, idx) => (
                  <Tooltip key={idx} title={issue.description}>
                    <span style={{ cursor: 'help' }}>{renderIssueTag(issue.tag)}</span>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}

          <div>
            <Title level={5}>Chi tiết nhận xét (Critiques):</Title>
            {result.critiques.length > 0 ? (
              <List
                size="small"
                dataSource={result.critiques}
                renderItem={item => (
                  <List.Item>
                    <Text><Tag color="orange" style={{ borderRadius: '50%', width: 20, height: 20, textAlign: 'center', padding: 0 }}>!</Tag> {item}</Text>
                  </List.Item>
                )}
              />
            ) : (
              <Alert message="Không có vấn đề gì đáng kể. Nội dung hiện tại đã khá tốt." type="success" showIcon />
            )}
          </div>

          <div>
            <Title level={5}>Gợi ý hiệu chỉnh (Suggested Comment):</Title>
            <div style={{
              padding: '12px',
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '8px',
              position: 'relative'
            }}>
              <Text italic>"{result.suggestedComment}"</Text>
            </div>
            <Text type="secondary" style={{ fontSize: '12px', marginTop: 8, display: 'block' }}>
              Bấm nút dưới đây để copy gợi ý này vào luồng trích xuất lại (Re-extract) toàn bộ panel cho scene.
            </Text>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default ReviewResultModal;
